import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from './cn'

export type Tone = 'info' | 'success' | 'warning' | 'danger'

const TONE = {
  info: { icon: Info, border: 'border-brand', ink: 'text-brand' },
  success: { icon: CircleCheck, border: 'border-success', ink: 'text-success' },
  warning: { icon: TriangleAlert, border: 'border-warning', ink: 'text-warning' },
  danger: { icon: CircleAlert, border: 'border-danger', ink: 'text-danger' },
} as const

/**
 * A message with a coloured edge and icon; the text itself stays in body ink,
 * so meaning never rests on colour alone (DESIGN.md status rule).
 *
 * `live` is for messages that answer something the visitor just did:
 * "assertive" (role=alert) for errors and changed prices, "polite"
 * (role=status) for good news. A notice that is simply part of the page (the
 * sample-data banner, payment information) has none, so it does not talk over the
 * heading that just received focus.
 */
export function Notice({
  tone = 'info',
  title,
  live,
  children,
  className,
}: {
  tone?: Tone
  title?: ReactNode
  live?: 'polite' | 'assertive'
  children?: ReactNode
  className?: string
}) {
  const { icon: Icon, border, ink } = TONE[tone]
  const role = live === 'assertive' ? 'alert' : live === 'polite' ? 'status' : undefined
  return (
    <div
      role={role}
      className={cn('flex gap-3 rounded-panel border-l-4 bg-surface p-4', border, className)}
    >
      <Icon aria-hidden="true" className={cn('mt-1 size-6 shrink-0', ink)} />
      <div className="flex flex-col gap-1 text-base text-text">
        {title ? <p className={cn('font-bold', ink)}>{title}</p> : null}
        {children}
      </div>
    </div>
  )
}
